import { authenticatedInstance } from '../index/index.service';
import type {
  ReportView,
  ReportSessionDate,
  ReportSessionOption,
  ReportRangeRequest,
  CategorySales,
  BestSellingProduct,
  MostCanceledProduct,
  BumpTimesSummary,
  ZReportLayoutConfigView,
  UpdateZReportLayoutConfigRequest,
} from 'shared-models/browser';
import { extractServiceErrorMessage } from '../shared/service-error.util';

export type {
  ReportView,
  ReportSessionDate,
  ReportSessionOption,
  ReportRangeRequest,
  CategorySales,
  BestSellingProduct,
  MostCanceledProduct,
  BumpTimesSummary,
  ZReportLayoutConfigView,
  UpdateZReportLayoutConfigRequest,
} from 'shared-models/browser';

class ReportsService {
  private employeeSessionHeaders(employeeSession?: string): { headers?: Record<string, string> } {
    if (!employeeSession) {
      return {};
    }
    return { headers: { 'x-employee-session': employeeSession } };
  }

  // Summary: דוח X — live snapshot of the open session.
  // Details: docs/function-library/web/services.md#reports-service
  async getX(tenantId: string, employeeSession?: string): Promise<ReportView> {
    try {
      const response = await authenticatedInstance.get<ReportView>(
        `/reports/tenant/${encodeURIComponent(tenantId)}/x`,
        this.employeeSessionHeaders(employeeSession),
      );
      return response.data;
    } catch (error: unknown) {
      throw new Error(extractServiceErrorMessage(error, ''));
    }
  }

  // Summary: דוח Z — final summary for one closed session.
  async getZ(tenantId: string, sessionId: number, employeeSession?: string): Promise<ReportView> {
    try {
      const response = await authenticatedInstance.get<ReportView>(
        `/reports/tenant/${encodeURIComponent(tenantId)}/z/${sessionId}`,
        this.employeeSessionHeaders(employeeSession),
      );
      return response.data;
    } catch (error: unknown) {
      throw new Error(extractServiceErrorMessage(error, ''));
    }
  }

  async getZLayoutConfig(tenantId: string, employeeSession?: string): Promise<ZReportLayoutConfigView> {
    try {
      const response = await authenticatedInstance.get<ZReportLayoutConfigView>(
        `/reports/tenant/${encodeURIComponent(tenantId)}/z-layout`,
        this.employeeSessionHeaders(employeeSession),
      );
      return response.data;
    } catch (error: unknown) {
      throw new Error(extractServiceErrorMessage(error, ''));
    }
  }

  async updateZLayoutConfig(
    tenantId: string,
    payload: UpdateZReportLayoutConfigRequest,
    employeeSession?: string,
  ): Promise<void> {
    try {
      await authenticatedInstance.patch(
        `/reports/tenant/${encodeURIComponent(tenantId)}/z-layout`,
        payload,
        this.employeeSessionHeaders(employeeSession),
      );
    } catch (error: unknown) {
      throw new Error(extractServiceErrorMessage(error, ''));
    }
  }

  // Summary: Wizard-open — selectable days; includeOpen adds the live shift (worker report).
  async listSessionDates(tenantId: string, employeeSession?: string, includeOpen?: boolean): Promise<ReportSessionDate[]> {
    try {
      const response = await authenticatedInstance.get<ReportSessionDate[]>(
        `/reports/tenant/${encodeURIComponent(tenantId)}/session-dates`,
        { params: { ...(includeOpen ? { includeOpen: 'true' } : {}) }, ...this.employeeSessionHeaders(employeeSession) },
      );
      return response.data;
    } catch (error: unknown) {
      throw new Error(extractServiceErrorMessage(error, ''));
    }
  }

  // Summary: One picked day's sessions + totalIncome; worker-scoped/open-inclusive per the flags.
  async getSessionsForDate(tenantId: string, date: string, employeeSession?: string, employeeId?: number, includeOpen?: boolean): Promise<ReportSessionOption[]> {
    try {
      const response = await authenticatedInstance.get<ReportSessionOption[]>(
        `/reports/tenant/${encodeURIComponent(tenantId)}/sessions-for-date`,
        {
          params: { date, ...(employeeId ? { employeeId } : {}), ...(includeOpen ? { includeOpen: 'true' } : {}) },
          ...this.employeeSessionHeaders(employeeSession),
        },
      );
      return response.data;
    } catch (error: unknown) {
      throw new Error(extractServiceErrorMessage(error, ''));
    }
  }

  // Summary: Restore-Z / worker report over chosen sessions (aggregate → 1, else per-session).
  async getRange(tenantId: string, payload: ReportRangeRequest, employeeSession?: string): Promise<ReportView[]> {
    try {
      const response = await authenticatedInstance.post<ReportView[]>(
        `/reports/tenant/${encodeURIComponent(tenantId)}/range`,
        payload,
        this.employeeSessionHeaders(employeeSession),
      );
      return response.data;
    } catch (error: unknown) {
      throw new Error(extractServiceErrorMessage(error, ''));
    }
  }

  // Summary: Real category sales (session-scoped/week/month) for the dashboard's קטגוריות section.
  async getCategorySales(tenantId: string, sessionIds: number[], employeeSession?: string): Promise<CategorySales[]> {
    try {
      const response = await authenticatedInstance.post<CategorySales[]>(
        `/reports/tenant/${encodeURIComponent(tenantId)}/category-sales`,
        { sessionIds },
        this.employeeSessionHeaders(employeeSession),
      );
      return response.data;
    } catch (error: unknown) {
      throw new Error(extractServiceErrorMessage(error, ''));
    }
  }

  // Summary: Top-selling products for the given sessions — dashboard's הנמכרים ביותר section.
  async getBestSellingProducts(tenantId: string, sessionIds: number[], employeeSession?: string): Promise<BestSellingProduct[]> {
    try {
      const response = await authenticatedInstance.post<BestSellingProduct[]>(
        `/reports/tenant/${encodeURIComponent(tenantId)}/best-selling`,
        { sessionIds },
        this.employeeSessionHeaders(employeeSession),
      );
      return response.data;
    } catch (error: unknown) {
      throw new Error(extractServiceErrorMessage(error, ''));
    }
  }

  // Summary: Most-canceled products for the given sessions — dashboard's המבוטלים ביותר section.
  async getMostCanceledProducts(tenantId: string, sessionIds: number[], employeeSession?: string): Promise<MostCanceledProduct[]> {
    try {
      const response = await authenticatedInstance.post<MostCanceledProduct[]>(
        `/reports/tenant/${encodeURIComponent(tenantId)}/most-canceled`,
        { sessionIds },
        this.employeeSessionHeaders(employeeSession),
      );
      return response.data;
    } catch (error: unknown) {
      throw new Error(extractServiceErrorMessage(error, ''));
    }
  }

  // Summary: First/last successful kitchen print for the given sessions — dashboard's זמני בון section.
  async getBumpTimes(tenantId: string, sessionIds: number[], employeeSession?: string): Promise<BumpTimesSummary> {
    try {
      const response = await authenticatedInstance.post<BumpTimesSummary>(
        `/reports/tenant/${encodeURIComponent(tenantId)}/bump-times`,
        { sessionIds },
        this.employeeSessionHeaders(employeeSession),
      );
      return response.data;
    } catch (error: unknown) {
      throw new Error(extractServiceErrorMessage(error, ''));
    }
  }
}

export const reportsService = new ReportsService();
